// public/js/modules/tenant-scope.js
// Global "active tenant" scope (docs/tenant-scope.md). One platform-wide choice
// of which tenant is currently displayed, applied by every list/map/query:
//
//   { all: true }          — everything the user may see (default on first login)
//   { tenantId: '<id>' }   — one tenant
//   { unassigned: true }   — platform-only: assets/devices without a tenant
//
// Persisted in localStorage (fast path, per device) AND mirrored to the
// user-settings table (follows the user across devices; wins on login).
// Changing the scope fires the 'tenantScopeChanged' window event — pages
// re-render/re-sync on it.
import { getCurrentUser, isAdminLevel } from './rbac.js';
import { loadTenants, loadTenantFieldConfig } from './tenants.js';

export const SCOPE_CHANGE_EVENT = 'tenantScopeChanged';
const LS_KEY = 'tenantScope';

let _scope = { all: true };
let _memberships = [];        // [{ tenantId, name, isDemo }] — the pickable tenants
let _isPlatform = false;      // platform roles see every tenant + "Unassigned"
let _ready = false;

function userId() {
  try { return getCurrentUser()?.userId || getCurrentUser()?.sub || null; } catch { return null; }
}

function readLS() {
  try {
    const raw = JSON.parse(localStorage.getItem(LS_KEY) || 'null');
    if (raw && raw.userId === userId() && raw.scope) return raw.scope;
  } catch (e) {}
  return null;
}
function writeLS(scope) {
  try { localStorage.setItem(LS_KEY, JSON.stringify({ userId: userId(), scope })); } catch (e) {}
}

function isValidScope(s) {
  if (!s || typeof s !== 'object') return false;
  if (s.all === true) return true;
  if (s.unassigned === true) return _isPlatform;
  if (s.tenantId) return _memberships.some(t => t.tenantId === s.tenantId);
  return false;
}

/**
 * Resolve memberships + the initial scope. Call once after login, BEFORE the
 * router loads the first page. `settings` is the already-fetched user-settings
 * object (app.js loads it at boot anyway) — its mirror wins over localStorage.
 */
export async function initTenantScope(settings = {}) {
  const groups = getCurrentUser()?.groups || [];
  _isPlatform = isAdminLevel(groups);

  try {
    if (_isPlatform) {
      const tenants = await loadTenants();
      _memberships = tenants.map(t => ({ tenantId: t.tenantId, name: t.name || t.tenantId, isDemo: t.isDemo === true }));
    } else {
      const tenants = await loadTenantFieldConfig();
      _memberships = tenants.map(t => ({ tenantId: t.tenantId, name: t.name || t.tenantId, isDemo: t.isDemo === true }));
    }
  } catch (e) {
    console.warn('[tenant-scope] membership load failed', e?.message);
    _memberships = [];
  }
  _memberships.sort((a, b) => String(a.name).localeCompare(String(b.name)));

  // user-settings mirror wins over localStorage; both must still be valid
  // (memberships can change between sessions). Fallback: All tenants.
  const candidates = [settings?.tenantScope, readLS()];
  _scope = candidates.find(isValidScope) || { all: true };

  // A single-tenant non-platform user is always scoped to that one tenant —
  // there is nothing to switch to, and single-scope UI (hidden tenant columns,
  // prefilled forms) is the better default for them.
  if (!_isPlatform && _memberships.length === 1) {
    _scope = { tenantId: _memberships[0].tenantId };
  }

  _ready = true;
  return _scope;
}

// ── Accessors ───────────────────────────────────────────────────────────────
export function getScope() { return { ..._scope }; }
export function isAllTenants() { return _scope.all === true; }
export function isUnassignedScope() { return _scope.unassigned === true; }
export function isScopeReady() { return _ready; }
export function getMemberships() { return [..._memberships]; }
export function isPlatformScope() { return _isPlatform; }

// Whether the user has anything to switch between (drives the widget UI).
export function isScopeSelectable() { return _isPlatform || _memberships.length > 1; }

// The ?tenant= value for scoped server loads: null (all) | tenantId | 'unassigned'.
export function scopeTenantParam() {
  if (_scope.unassigned) return 'unassigned';
  return _scope.tenantId || null;
}

// Stable cache-partition key for the delta cache: 'all' | 't:<id>' | 'unassigned'.
export function scopeKey() {
  if (_scope.unassigned) return 'unassigned';
  return _scope.tenantId ? `t:${_scope.tenantId}` : 'all';
}

// Display label for the widget / chip (blurred by demo mode via .tenant-name).
export function scopeLabel() {
  if (_scope.unassigned) return 'Unassigned';
  if (_scope.tenantId) {
    return _memberships.find(t => t.tenantId === _scope.tenantId)?.name || _scope.tenantId;
  }
  return 'All tenants';
}

// Client-side check for entities carrying a tenantId (POIs, alerts, users, …).
export function inScope(entity) {
  if (_scope.all) return true;
  const tid = entity?.tenantId ?? null;
  if (_scope.unassigned) return !tid || tid === 'unassigned';
  return tid === _scope.tenantId;
}

// ── Mutation ────────────────────────────────────────────────────────────────
/**
 * @param {object} next
 * @param {{reason?: 'user'|'auto'}} [opts]
 *   'auto' marks a switch the APP made on the user's behalf - today only
 *   ensureScopeFor() following a deep link. Listeners need to tell the two
 *   apart: a user picking a tenant while an asset detail page is open must
 *   leave that page, whereas an auto-switch exists precisely to keep it open.
 */
export function setScope(next, opts = {}) {
  if (!isValidScope(next)) { console.warn('[tenant-scope] invalid scope ignored', next); return false; }
  const changed = scopeKeyOf(next) !== scopeKey();
  _scope = { ...next };
  writeLS(_scope);
  // Mirror to user-settings (partial PATCH — never clobbers other settings).
  // Fire-and-forget: a failed mirror only costs cross-device sync.
  try {
    window.authenticatedFetch?.('api/v1/settings', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenantScope: _scope }),
    }).catch(() => {});
  } catch (e) {}
  if (changed) window.dispatchEvent(new CustomEvent(SCOPE_CHANGE_EVENT,
    { detail: { scope: getScope(), reason: opts.reason === 'auto' ? 'auto' : 'user' } }));
  return true;
}

function scopeKeyOf(s) {
  if (s.unassigned) return 'unassigned';
  return s.tenantId ? `t:${s.tenantId}` : 'all';
}

/**
 * Deep-link helper: make sure the given tenant is visible. If the current
 * scope excludes it and the user may see it, the scope AUTO-SWITCHES to that
 * tenant (locked decision — alert links etc. must just work). Returns true
 * when the entity is (now) in scope.
 */
export function ensureScopeFor(tenantId) {
  if (_scope.all) return true;
  const tid = tenantId ?? null;
  if (_scope.unassigned && (!tid || tid === 'unassigned')) return true;
  if (_scope.tenantId && _scope.tenantId === tid) return true;
  if (tid && tid !== 'unassigned' && _memberships.some(t => t.tenantId === tid)) {
    setScope({ tenantId: tid }, { reason: 'auto' });
    window.showToast?.(`Tenant scope switched`, 'info');
    return true;
  }
  if ((!tid || tid === 'unassigned') && _isPlatform) {
    setScope({ unassigned: true }, { reason: 'auto' });
    window.showToast?.(`Tenant scope switched`, 'info');
    return true;
  }
  return false;
}

export function onScopeChange(cb) {
  const h = () => cb(getScope());
  window.addEventListener(SCOPE_CHANGE_EVENT, h);
  return () => window.removeEventListener(SCOPE_CHANGE_EVENT, h);
}
